import { forwardRef, useImperativeHandle } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Persona } from '../../types';
import Input from '../Forms/Input';

type PersonaFormData = Omit<Persona, 'id' | 'objectif' | 'frustration'> & {
  objectif: string;
  frustration: string;
};

interface PersonaFormProps {
  onSubmit: (data: Partial<Persona>) => void;
  initialData?: Partial<Persona>;
}

export interface PersonaFormHandle {
  submit: () => void;
}

const PersonaForm = forwardRef<PersonaFormHandle, PersonaFormProps>(
  ({ onSubmit, initialData }, ref) => {
    const { t } = useTranslation();

    const defaultValues: Partial<PersonaFormData> = {
      ...initialData,
      objectif: initialData?.objectif || '',
      frustration: initialData?.frustration || '',
    };

    const { register, handleSubmit, formState: { errors } } = useForm<PersonaFormData>({
      defaultValues,
    });

    const handleFormSubmit: SubmitHandler<PersonaFormData> = (data) => {
      const submitData: Partial<Persona> = {
        name: data.name,
        role: data.role,
        citation: data.citation,
        objectif: data.objectif,
        frustration: data.frustration,
        description: data.description,
      };
      onSubmit(submitData);
    };

    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(handleFormSubmit)();
      },
    }));

    return (
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        {/* Name and Role on the same line */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t('persona.form.name')}
            id="persona-name"
            name="name"
            register={register}
            rules={{ required: t('persona.form.nameRequired') }}
            error={errors.name}
          />
          <Input
            label={t('persona.form.role')}
            id="persona-role"
            name="role"
            register={register}
            rules={{ required: t('persona.form.roleRequired') }}
            error={errors.role}
          />
        </div>
        {/* Objectives - Simple textarea */}
        <div>
          <label htmlFor="persona-objectif" className="block text-sm font-medium text-gray-400 mb-1">
            {t('persona.form.objectives')}
          </label>
          <textarea
            id="persona-objectif"
            rows={2}
            placeholder={t('persona.form.objectivesPlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            {...register('objectif')}
          />
        </div>

        {/* Frustrations - Simple textarea */}
        <div>
          <label htmlFor="persona-frustration" className="block text-sm font-medium text-gray-400 mb-1">
            {t('persona.form.frustrations')}
          </label>
          <textarea
            id="persona-frustration"
            rows={2}
            placeholder={t('persona.form.frustrationsPlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            {...register('frustration')}
          />
        </div>

        {/* Citation - Simple textarea */}
        <div>
          <label htmlFor="persona-citation" className="block text-sm font-medium text-gray-400 mb-1">
            {t('persona.form.citation')}
          </label>
          <textarea
            id="persona-citation"
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            {...register('citation')}
          />
        </div>

        {/* Description - Simple textarea */}
        <div>
          <label htmlFor="persona-description" className="block text-sm font-medium text-gray-400 mb-1">
            {t('persona.form.description')}
          </label>
          <textarea
            id="persona-description"
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            {...register('description')}
          />
        </div>


      </form>
    );
  }
);

PersonaForm.displayName = 'PersonaForm';

export default PersonaForm;
